import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Box,
  Typography
} from '@mui/material';
import {
  Flag as FlagIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import requestDeduplicator from '../utils/requestDeduplicator';
import {
  getReviewReportStatusFromCache,
  setReviewReportStatusCache,
  getShortCacheTtl
} from '../services/cache/reviewCacheService';

/**
 * Composant réutilisable pour signaler un avis
 * 
 * @param {Object} props
 * @param {string} props.reviewType - Type d'avis ('reservation_review', 'owner_review', 'owner_response')
 * @param {number} props.reviewId - ID de l'avis
 * @param {string} [props.variant] - 'icon' ou 'button' (défaut: 'icon')
 * @param {string} [props.size] - Taille du bouton ('small', 'medium', 'large')
 * @param {boolean} [props.disabled] - Désactiver le bouton
 * @param {Function} [props.onReported] - Callback après signalement réussi
 */
const ReportReviewButton = ({
  reviewType,
  reviewId,
  variant = 'icon',
  size = 'small',
  disabled = false,
  onReported
}) => {
  const { t } = useTranslation('reports');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Vérifier si l'utilisateur a déjà signalé cet avis
  useEffect(() => {
    let isMounted = true;

    const checkReportStatus = async () => {
      const cacheKey = `${reviewType}:${reviewId}`;
      const cachedStatus = getReviewReportStatusFromCache(cacheKey);
      if (cachedStatus !== null) {
        setHasReported(!!cachedStatus.hasReported);
        setCheckingStatus(false);
        return;
      }

      try {
        setCheckingStatus(true);
        
        // Utiliser le deduplicator pour éviter les appels concurrents
        const dedupKey = `check-report:${cacheKey}`;
        const response = await requestDeduplicator.fetch(
          dedupKey,
          () => api.get('/review-reports/check', {
            params: { reviewType, reviewId }
          }),
          { debug: process.env.NODE_ENV === 'development' }
        );
        
        if (!isMounted) {
          return;
        }
        const hasReportedResponse = response.data.data.hasReported;
        setHasReported(hasReportedResponse);
        setReviewReportStatusCache(cacheKey, { hasReported: hasReportedResponse });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erreur lors de la vérification du signalement:', err);
        }
        if (!isMounted) {
          return;
        }
        setHasReported(false);
        setReviewReportStatusCache(cacheKey, { hasReported: false }, getShortCacheTtl());
      } finally {
        if (isMounted) {
          setCheckingStatus(false);
        }
      }
    };

    if (reviewType && reviewId) {
      checkReportStatus();
    }

    return () => {
      isMounted = false;
    };
  }, [reviewType, reviewId]);

  const handleOpen = () => {
    setOpen(true);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    setOpen(false);
    setReason('');
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async () => {
    // Validation
    if (!reason.trim()) {
      setError(t('report.error.reason_required', 'Veuillez saisir une raison'));
      return;
    }

    if (reason.trim().length < 10) {
      setError(t('report.error.reason_too_short', 'La raison doit contenir au moins 10 caractères'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/review-reports', {
        reviewType,
        reviewId,
        reason: reason.trim()
      });

      const cacheKey = `${reviewType}:${reviewId}`;
      setSuccess(true);
      setHasReported(true);
      setReviewReportStatusCache(cacheKey, { hasReported: true });

      // Attendre 1.5s pour afficher le message de succès
      setTimeout(() => {
        handleClose();
        if (onReported) {
          onReported(response.data.data);
        }
      }, 1500);

    } catch (err) {
      console.error('Erreur lors du signalement:', err);
      
      const cacheKey = `${reviewType}:${reviewId}`;
      if (err.response?.status === 409) {
        // Utiliser le message du backend (déjà traduit) ou fallback
        setError(err.response?.data?.message || t('report.error.already_reported', 'Vous avez déjà signalé cet avis'));
        setHasReported(true);
        setReviewReportStatusCache(cacheKey, { hasReported: true });
      } else if (err.response?.status === 429) {
        // Utiliser le message du backend (déjà traduit avec les valeurs dynamiques)
        setError(err.response?.data?.message || t('report.error.too_many_reports', 'Trop de signalements effectués récemment. Veuillez réessayer plus tard.'));
        setReviewReportStatusCache(cacheKey, { hasReported: false }, getShortCacheTtl());
      } else if (err.response?.status === 403) {
        // Utiliser le message du backend (déjà traduit) ou fallback
        setError(err.response?.data?.message || t('report.error.own_review', 'Vous ne pouvez pas signaler votre propre avis'));
      } else {
        setError(
          err.response?.data?.message ||
          t('report.error.generic', 'Erreur lors du signalement. Veuillez réessayer.')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Si en cours de vérification, afficher un spinner
  if (checkingStatus) {
    return variant === 'icon' ? (
      <IconButton size={size} disabled>
        <CircularProgress size={20} />
      </IconButton>
    ) : (
      <Button size={size} disabled startIcon={<CircularProgress size={20} />}>
        {t('report.checking', 'Vérification...')}
      </Button>
    );
  }

  // Si déjà signalé, afficher l'état
  if (hasReported) {
    return variant === 'icon' ? (
      <Tooltip title={t('report.already_reported', 'Vous avez déjà signalé cet avis')}>
        <IconButton size={size} disabled>
          <FlagIcon color="warning" />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        size={size}
        disabled
        startIcon={<FlagIcon />}
        color="warning"
      >
        {t('report.reported', 'Signalé')}
      </Button>
    );
  }

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title={t('report.button_tooltip', 'Signaler cet avis')}>
          <IconButton
            size={size}
            onClick={handleOpen}
            disabled={disabled}
            color="default"
          >
            <FlagIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size={size}
          onClick={handleOpen}
          disabled={disabled}
          startIcon={<FlagIcon />}
          variant="outlined"
          color="warning"
        >
          {t('report.button', 'Signaler')}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagIcon color="warning" />
            <Typography variant="h6">
              {t('report.dialog_title', 'Signaler cet avis')}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('report.success', 'Signalement enregistré avec succès. Merci pour votre contribution.')}
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {t('report.dialog_description', 
                  'Veuillez expliquer pourquoi cet avis devrait être examiné par notre équipe de modération. Soyez le plus précis possible.'
                )}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                autoFocus
                fullWidth
                multiline
                rows={4}
                label={t('report.reason_label', 'Raison du signalement')}
                placeholder={t('report.reason_placeholder', 
                  'Ex: Contenu inapproprié, langage offensant, fausses informations...'
                )}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={loading}
                error={!reason.trim() && error}
                helperText={
                  reason.trim().length > 0 
                    ? `${reason.trim().length} / 500 caractères`
                    : t('report.reason_helper', 'Minimum 10 caractères')
                }
                inputProps={{
                  maxLength: 500
                }}
              />
            </>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleClose}
              disabled={loading}
              color="inherit"
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !reason.trim() || reason.trim().length < 10}
              variant="contained"
              color="warning"
              startIcon={loading ? <CircularProgress size={20} /> : <FlagIcon />}
            >
              {loading
                ? t('report.submitting', 'Envoi...')
                : t('report.submit', 'Signaler')
              }
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ReportReviewButton;
